import React, { useEffect, useState } from "react";
import API from "./adminAxios";
import { toast } from "react-toastify";
import { FaShieldAlt, FaUsers, FaSearch, FaCogs, FaEdit, FaLock, FaSnowflake, FaTrashAlt } from "react-icons/fa";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [selectedAdminFilter, setSelectedAdminFilter] = useState("ALL");

  const [showEditModal, setShowEditModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, title: "", onConfirm: null });
  const [selectedUser, setSelectedUser] = useState(null);
  
  const [formData, setFormData] = useState({ 
  username: "", email: "", password: "", newPin: "", registryKey: "", maxAllowedSessions: 1
});

  const fetchData = async () => {
    try {
      const [uRes, aRes, settingsRes] = await Promise.allSettled([
        API.get("/super-admin/list-users"),
        API.get("/super-admin/list-admins"),
        API.get("/super-admin/list-nodes").catch(() => API.get("/super-admin/settings"))
      ]);

      let loadedUsers = uRes.status === 'fulfilled' ? (uRes.value.data || []) : [];
      let loadedAdmins = aRes.status === 'fulfilled' ? (aRes.value.data || []) : [];

      if (settingsRes.status === 'fulfilled' && settingsRes.value.data) {
        const nodeData = settingsRes.value.data;
        const extraNodes = Array.isArray(nodeData) ? nodeData : [nodeData];
        extraNodes.forEach(node => {
          const rKey = node.registryKey || node.registrationKey || node.registryCode;
          if (rKey && !loadedAdmins.some(a => (a.registryKey || a.registryCode || a.registrationKey) === rKey)) {
            loadedAdmins.push({
              _id: node._id || `node_${rKey}`,
              username: node.username || node.name || `Admin (${rKey})`,
              email: node.email || node.nodeSupportContact || `${rKey.toLowerCase()}@system.node`,
              registryKey: rKey,
              isActive: true,
              role: 'admin',
              isDynamicNode: true
            });
          }
        });
      }

      setUsers(loadedUsers);
      setAdmins(loadedAdmins);
    } catch (err) { 
      toast.error("Database Sync Failed"); 
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => { 
    fetchData(); 
    const closeMenu = () => setActiveDropdown(null);
    window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, []);

  const askConfirmation = (title, action) => {
    setConfirmDialog({ show: true, title, onConfirm: action });
    setActiveDropdown(null);
  };

  const handleAction = async (path) => {
    try {
      await API.patch(path);
      toast.success("Command Executed Successfully");
      fetchData();
    } catch (err) { 
      toast.error(err.response?.data?.message || "Command Failed"); 
    }
    setConfirmDialog({ show: false, title: "", onConfirm: null });
  };

 const openEdit = (item) => {
  setSelectedUser(item);
  setFormData({
    username: item.username || item.name || "",
    email: item.email || "",
    newPin: item.withdrawPassword || item.securityPin || "", // Map correctly to newPin
    registryKey: item.registryKey || item.registryCode || item.registrationKey || "",
    maxAllowedSessions: item.maxAllowedSessions || 1,
    password: "" 
  });
  setShowEditModal(true);
  setActiveDropdown(null);
};

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const isAd = selectedUser?.role === 'admin' || selectedUser?.isDynamicNode;
      const endpoint = isAd 
        ? `/super-admin/update-admin/${selectedUser._id}` 
        : `/super-admin/update-user/${selectedUser._id}`;
      
      await API.put(endpoint, formData);
      toast.success("Profile Updated Successfully");
      setShowEditModal(false);
      fetchData();
    } catch (err) { 
      try {
        await API.put(`/super-admin/update-user/${selectedUser._id}`, formData);
        toast.success("Profile Updated Successfully");
        setShowEditModal(false);
        fetchData();
      } catch (innerErr) {
        toast.error(innerErr.response?.data?.message || err.response?.data?.message || "Update failed"); 
      }
    }
  };

  const StatusDot = ({ active, label }) => (
    <div style={styles.statusGroup}>
      <div style={{
        ...styles.dot,
        backgroundColor: active ? "#10b981" : "#ef4444",
        boxShadow: active ? "0 0 8px #10b981" : "0 0 8px #ef4444"
      }} />
      <span style={styles.dotLabel}>{label}</span>
    </div>
  );

  const ActionMenu = ({ item, isUser }) => (
    <div style={styles.dropdownContainer} onClick={(e) => e.stopPropagation()}>
      <button 
        style={styles.dropTrigger} 
        onClick={() => setActiveDropdown(activeDropdown === item._id ? null : item._id)}
      >
        <FaCogs style={{marginRight: '6px'}} /> ACTIONS
      </button>
      {activeDropdown === item._id && (
        <div style={styles.menuItems}>
          <button onClick={() => openEdit(item)} style={styles.menuBtn}>
            <FaEdit /> Edit Profile
          </button>
          
          <button onClick={() => askConfirmation(`Toggle Access for ${item.username || item.name || item.email}?`, () => handleAction(`/super-admin/toggle-active/${item._id}`))} style={styles.menuBtn}>
            <FaLock /> Toggle Access
          </button>

          {isUser && (
            <button 
              onClick={() => askConfirmation(`Toggle Liquidity for ${item.username || item.name || item.email}?`, () => handleAction(`/super-admin/toggle-freeze-withdraw/${item._id}`))} 
              style={styles.menuBtn}
            >
              <FaSnowflake /> Toggle Liquidity
            </button>
          )}
          
          {!item.isDynamicNode && (
            <button onClick={() => askConfirmation(`DELETE ${item.username || item.name || item.email} PERMANENTLY?`, async () => {
               try {
                  const deleteEndpoint = isUser 
                    ? `/super-admin/delete-user/${item._id}` 
                    : `/super-admin/delete-admin/${item._id}`;
                  await API.delete(deleteEndpoint);
                  toast.success("Account Deleted Successfully");
                  fetchData();
               } catch(e) { 
                  toast.error(e.response?.data?.message || "Delete failed"); 
               }
               setConfirmDialog({show: false, title: "", onConfirm: null});
            })} style={{...styles.menuBtn, color: '#ef4444'}}>
              <FaTrashAlt /> Delete Account
            </button>
          )}
        </div>
      )}
    </div>
  );

  const filteredAdmins = admins.filter(a => {
    const uName = a.username || a.name || "";
    const uEmail = a.email || "";
    const uReg = a.registryKey || a.registryCode || a.registrationKey || "";
    const q = searchQuery.toLowerCase();
    return uName.toLowerCase().includes(q) || uEmail.toLowerCase().includes(q) || uReg.toLowerCase().includes(q);
  });

  const filteredUsers = users.filter(u => {
    const uName = u.username || u.name || "";
    const uEmail = u.email || "";
    const uReg = u.registryKey || u.registryCode || u.registrationKey || u.assignedNode || "";
    const q = searchQuery.toLowerCase();
    
    const matchesSearch = uName.toLowerCase().includes(q) || uEmail.toLowerCase().includes(q) || uReg.toLowerCase().includes(q);
    const matchesAdmin = 
      selectedAdminFilter === "ALL" || 
      u.registryKey === selectedAdminFilter || 
      u.registryCode === selectedAdminFilter || 
      u.registrationKey === selectedAdminFilter || 
      u.assignedNode === selectedAdminFilter;

    return matchesSearch && matchesAdmin;
  });

  if (loading) return <div style={styles.loading}>INITIALIZING HUB...</div>;

  return (
    <div style={styles.wrapper}>
      <header style={styles.header}>
        <h2 style={styles.mainTitle}>OPERATIONS HUB</h2>
        <div style={styles.searchWrapper}>
          <FaSearch style={styles.searchIcon} />
          <input 
            style={styles.search} 
            placeholder="Search by username, email, or registry..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
      </header>

      {/* ADMIN SECTION */}
      <section style={styles.section}>
        <div style={styles.secHeader}><FaShieldAlt style={{color: '#3b82f6'}} /> ADMINISTRATIVE STAFF</div>
        <div style={styles.tableResponsive}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ADMIN IDENTIFIER</th>
                <th style={styles.th}>REGISTRY</th>
                <th style={styles.th}>SYSTEM ACCESS</th>
                <th style={{...styles.th, textAlign: 'right'}}>MANAGEMENT</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdmins.map(a => {
                const displayName = a.username || a.name || (a.email ? a.email.split('@')[0] : "Admin");
                const regKey = a.registryKey || a.registryCode || a.registrationKey || "UNASSIGNED";
                return (
                  <tr key={a._id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={styles.userName}>{displayName}</div>
                      <div style={styles.userEmail}>{a.email || "system@node.internal"}</div>
                    </td>
                    <td style={styles.td}><span style={styles.tag}>{regKey}</span></td>
                    <td style={styles.td}><StatusDot active={a.isActive ?? true} label="Login" /></td>
                    <td style={{...styles.td, textAlign: 'right'}}><ActionMenu item={a} isUser={false} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* USER SECTION */}
      <section style={styles.section}>
        <div style={{...styles.secHeader, borderLeftColor: '#10b981', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
            <FaUsers style={{color: '#10b981'}} /> CLIENT PORTFOLIO
          </div>
          
          <select 
            value={selectedAdminFilter} 
            onChange={(e) => setSelectedAdminFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="ALL">ALL ADMINS</option>
            {admins.map(a => {
              const regKey = a.registryKey || a.registryCode || a.registrationKey || "";
              const adminName = a.username || a.name || (a.email ? a.email.split('@')[0] : "Admin");
              return (
                <option key={a._id} value={regKey}>
                  {adminName} ({regKey})
                </option>
              );
            })}
          </select>
        </div>
        <div style={styles.tableResponsive}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>CLIENT</th>
                <th style={styles.th}>LIQUIDITY / BALANCE</th>
                <th style={styles.th}>REGISTRY / NODE</th>
                <th style={styles.th}>PERMISSIONS</th>
                <th style={{...styles.th, textAlign: 'right'}}>CONTROL</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(u => {
                const uName = u.username || u.name || (u.email ? u.email.split('@')[0] : "Client");
                const uBalance = u.creditBalance ?? u.balance ?? u.walletBalance ?? u.credit ?? 0;
                const uReg = u.assignedNode || u.registryKey || u.registryCode || u.registrationKey || "UNASSIGNED";
                const isLiquidityActive = u.withdrawOpen ?? u.liquidityOpen ?? u.liquidity ?? u.isLiquidityActive ?? true;
                
                return (
                  <tr key={u._id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={styles.userName}>{uName}</div>
                      <div style={styles.userEmail}>{u.email}</div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.price}>
                          ${Number(uBalance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{...styles.tag, color: '#10b981', background: 'rgba(16, 185, 129, 0.1)'}}>
                        {uReg}
                      </span>
                    </td>
                    <td style={styles.td}>
                        <div style={{display: 'flex', gap: '15px'}}>
                          <StatusDot active={u.isActive ?? true} label="Auth" />
                          <StatusDot active={isLiquidityActive} label="Assets" />
                        </div>
                    </td>
                    <td style={{...styles.td, textAlign: 'right'}}><ActionMenu item={u} isUser={true} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* CONFIRMATION OVERLAY */}
      {confirmDialog.show && (
        <div style={styles.overlay}>
          <div style={styles.confirmBox}>
            <div style={styles.alertIcon}>!</div>
            <h3 style={styles.modalTitle}>System Protocol</h3>
            <p style={styles.modalSub}>{confirmDialog.title}</p>
            <div style={styles.modalActionRow}>
              <button style={styles.confirmBtn} onClick={confirmDialog.onConfirm}>AUTHORIZE ACTION</button>
              <button style={styles.cancelBtn} onClick={() => setConfirmDialog({show: false, title: "", onConfirm: null})}>ABORT</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <header style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Identity Management</h3>
              <p style={styles.modalSub}>Updating credentials for <span style={{color: '#38bdf8', fontWeight: '700'}}>{selectedUser?.username || selectedUser?.name || selectedUser?.email}</span></p>
            </header>
            <form onSubmit={saveEdit} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Username</label>
                <input style={styles.input} value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} />
              </div>

              {(selectedUser?.role === 'admin' || selectedUser?.isDynamicNode) ? (
                <>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Admin Email</label>
                    <input style={styles.input} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Reset Password (Forces Logout)</label>
                    <input type="password" placeholder="New Password" style={styles.input} value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Registry Assignment</label>
                    <input style={{...styles.input, color: '#3b82f6'}} value={formData.registryKey} onChange={e => setFormData({...formData, registryKey: e.target.value})} />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Max Allowed Login Devices</label>
                    <input 
                      type="number" 
                      min="1" 
                      max="5"
                      style={{...styles.input, color: '#10b981'}} 
                      value={formData.maxAllowedSessions} 
                      onChange={e => setFormData({...formData, maxAllowedSessions: Number(e.target.value)})} 
                    />
                  </div>
                </>
              ) : (
                <>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Change User Password (Forces Instant Logout)</label>
                    <input type="password" placeholder="Leave blank to keep current" style={styles.input} value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
                  </div>
                  <div style={styles.inputGroup}>
  <label style={styles.label}>Withdrawal Security Code</label>
  <input 
    placeholder="Enter new withdraw code or clear" 
    style={{...styles.input, color: '#fbbf24'}} 
    value={formData.newPin} 
    onChange={e => setFormData({...formData, newPin: e.target.value})} 
  />
  <small style={{ color: "#64748b", fontSize: "11px" }}>If changed, user withdrawals will fail until provided with the new code.</small>
</div>
                </>
              )}

              <div style={styles.modalActionRow}>
                <button type="submit" style={styles.saveBtn}>Apply Changes</button>
                <button type="button" onClick={() => setShowEditModal(false)} style={styles.cancelBtn}>Close</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  wrapper: { padding: '40px 20px', maxWidth: '1150px', margin: '0 auto', fontFamily: "'Inter', sans-serif" },
  header: { marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' },
  mainTitle: { fontSize: '24px', fontWeight: '800', color: '#fff', margin: 0, letterSpacing: '1px' },
  searchWrapper: { position: 'relative', width: '100%', maxWidth: '400px' },
  searchIcon: { position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' },
  search: { width: '100%', padding: '14px 14px 14px 45px', background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '14px', color: '#fff', outline: 'none', boxSizing: 'border-box' },
  section: { background: 'rgba(30, 41, 59, 0.3)', backdropFilter: 'blur(10px)', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.05)', marginBottom: '40px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' },
  secHeader: { padding: '20px 25px', fontWeight: '700', fontSize: '14px', color: '#f8fafc', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', borderLeft: '4px solid #3b82f6', display: 'flex', alignItems: 'center', gap: '10px' },
  filterSelect: { background: 'rgba(15, 23, 42, 0.9)', color: '#38bdf8', border: '1px solid rgba(255, 255, 255, 0.15)', padding: '8px 14px', borderRadius: '10px', outline: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  tableResponsive: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '15px 25px', fontSize: '11px', color: '#94a3b8', textAlign: 'left', background: 'rgba(0, 0, 0, 0.2)', textTransform: 'uppercase', letterSpacing: '1px' },
  tr: { borderBottom: '1px solid rgba(255, 255, 255, 0.03)', transition: 'background 0.2s' },
  td: { padding: '18px 25px', fontSize: '14px', color: '#e2e8f0' },
  userName: { fontWeight: '700', color: '#fff' },
  userEmail: { fontSize: '12px', color: '#64748b', marginTop: '2px' },
  tag: { padding: '4px 10px', background: 'rgba(59, 130, 246, 0.1)', color: '#60a5fa', borderRadius: '8px', fontSize: '11px', fontWeight: '700' },
  price: { color: '#10b981', fontWeight: '800' },
  statusGroup: { display: 'flex', alignItems: 'center', gap: '8px' },
  dot: { width: '8px', height: '8px', borderRadius: '50%' },
  dotLabel: { fontSize: '11px', color: '#94a3b8', fontWeight: '600' },
  dropdownContainer: { position: 'relative' },
  dropTrigger: { background: 'rgba(255, 255, 255, 0.04)', color: '#cbd5e1', border: '1px solid rgba(255, 255, 255, 0.1)', padding: '8px 14px', borderRadius: '10px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center' },
  menuItems: { position: 'absolute', right: 0, top: '110%', background: '#1e293b', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '14px', zIndex: 100, width: '200px', boxShadow: '0 20px 40px rgba(0,0,0,0.6)', padding: '8px' },
  menuBtn: { width: '100%', padding: '12px 15px', background: 'none', border: 'none', color: '#e2e8f0', textAlign: 'left', cursor: 'pointer', fontSize: '12px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', transition: 'background 0.2s' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' },
  confirmBox: { background: '#0f172a', padding: '35px', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.1)', width: '100%', maxWidth: '400px', textAlign: 'center', boxShadow: '0 25px 50px rgba(0,0,0,0.7)' },
  alertIcon: { width: '50px', height: '50px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: 'bold', margin: '0 auto 20px', border: '1px solid #ef4444' },
  modal: { background: '#0f172a', padding: '35px', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.1)', width: '100%', maxWidth: '440px', boxShadow: '0 25px 50px rgba(0,0,0,0.7)' },
  modalHeader: { marginBottom: '20px' },
  modalTitle: { fontSize: '18px', fontWeight: '800', color: '#fff', margin: '0 0 6px 0' },
  modalSub: { color: '#94a3b8', fontSize: '13px', margin: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '11px', fontWeight: '700', color: '#64748b', letterSpacing: '1px' },
  input: { padding: '12px 14px', background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#fff', outline: 'none', borderRadius: '10px', fontSize: '14px' },
  modalActionRow: { display: 'flex', gap: '12px', marginTop: '15px' },
  confirmBtn: { flex: 1, padding: '12px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', fontSize: '13px', transition: 'background 0.2s' },
  saveBtn: { flex: 1, padding: '12px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', fontSize: '13px' },
  cancelBtn: { flex: 1, padding: '12px', background: 'rgba(255,255,255,0.05)', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', fontSize: '13px' },
  loading: { display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: '#020617', color: '#00f2ff', fontWeight: 'bold', letterSpacing: '2px' }
};

export default UserList;